/**
 * ПОЧЕМУ ОСТАЛЬНЫЕ НЕ ИЗМЕНИЛИСЬ — а не только сколько их.
 *
 * Донор (PRO100-онлайн, `src/ui/BulkEditPanel.tsx`): «Панель показывает РЕЗУЛЬТАТ целиком, а не
 * только успех: сколько применено и почему пропущены остальные. Массовая операция,
 * отчитавшаяся одним „готово", оставляет человека гадать, покрашены ли три шкафа без фасадов —
 * а они не покрашены и не могли быть.»
 *
 * Во вьюере причины УЖЕ ПРИХОДИЛИ с сервера — `results[].error` по каждой детали — и
 * выбрасывались: отчёт говорил «Изменено 7/12 · Часть панелей не была затронута». Человек
 * оставался с вопросом, который решает, что делать дальше: «не подошёл материал», «деталь
 * заблокирована» и «детали больше нет» требуют трёх разных действий, а выглядели одинаково.
 *
 * ПРИЧИНЫ НЕ ПЕРЕПИСЫВАЮТСЯ. Здесь их только считают и группируют: свой словарь разошёлся бы с
 * текстами сервера, и человек увидел бы два разных объяснения одного отказа.
 */
import { pluralRu } from '@/shared/lib/issue-grouping';

export type BulkPanelResult = Readonly<{ panelId: string; ok: boolean; error?: string }>;

export type BulkSkipGroup = Readonly<{ reason: string; panelIds: readonly string[] }>;

export type BulkApplyReport = Readonly<{
  applied: number;
  total: number;
  skipped: readonly BulkSkipGroup[];
}>;

/** Когда сервер отказал, но промолчал о причине: молчание тоже надо назвать. */
export const UNNAMED_SKIP_REASON = 'причина не названа сервером';

/** Сколько разных причин показывать: отчёт должен читаться, а не листаться. */
const MAX_REASONS_SHOWN = 3;

export function bulkApplyReport(
  results: readonly BulkPanelResult[] | null | undefined,
  applied?: number,
  total?: number,
): BulkApplyReport {
  const rows = Array.isArray(results) ? results : [];
  const groups = new Map<string, string[]>();
  for (const row of rows) {
    if (!row || row.ok) continue;
    const reason = String(row.error ?? '').trim() || UNNAMED_SKIP_REASON;
    const bucket = groups.get(reason);
    if (bucket) bucket.push(String(row.panelId ?? ''));
    else groups.set(reason, [String(row.panelId ?? '')]);
  }
  const skipped = [...groups.entries()]
    .map(([reason, panelIds]) => Object.freeze({ reason, panelIds: Object.freeze([...panelIds]) }))
    /* Самая частая причина — первой: она и есть то, с чем человеку разбираться. */
    .sort((left, right) => right.panelIds.length - left.panelIds.length || left.reason.localeCompare(right.reason));
  return Object.freeze({
    applied: Number.isFinite(applied) ? Number(applied) : rows.filter((row) => row?.ok).length,
    total: Number.isFinite(total) ? Number(total) : rows.length,
    skipped: Object.freeze(skipped),
  });
}

export function describeBulkApplyReport(report: BulkApplyReport): string {
  if (!report.skipped.length) return 'Все панели обновлены';
  const shown = report.skipped.slice(0, MAX_REASONS_SHOWN)
    .map((group) => `«${group.reason}» — ${group.panelIds.length} дет.`);
  const hidden = report.skipped.length - shown.length;
  /* Скрытые причины ПЕРЕСЧИТЫВАЮТСЯ, а не прячутся молча: «и ещё 2 причины» — это приглашение
     посмотреть, а исчезнувший хвост — обещание, что причин было всего три. */
  if (hidden > 0) shown.push(`и ещё ${hidden} ${pluralRu(hidden, 'причина', 'причины', 'причин')}`);
  return `Не затронуто: ${shown.join('; ')}`;
}
